import React, { useRef, useState, useEffect } from 'react'
import { withTheme, createComponent } from '@ycos/fela'
import { Functional } from '@ycos/component-columns'
import Slider from 'react-slick'
import { hasTag } from '@ycos/coremedia-renderer'
import useScreenBreakpoint from '@ycos/use-screen-breakpoint'

export const AUTO_PLAY_SPEED = 6000
export const AUTO_PLAY_DELAY = 6000

const DotsContainer = createComponent(
  'DotsContainer',
  ({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginBottom: `${2 * theme.spacingMultiplier}px`,
  }),
  'ul'
)

const Dot = createComponent('Dot', ({ active, theme, index, lastItem }) => {
  const isRtl = theme.direction === 'rtl'
  const lastItemMargin = isRtl ? `${2 * theme.spacingMultiplier}px` : 0
  return {
    display: 'block',
    width: '6px',
    height: '6px',
    background: active ? theme.typography.colors.PrimaryBlack : '#cbcbcb',
    borderRadius: '8px',
    marginRight: index === lastItem ? lastItemMargin : `${2 * theme.spacingMultiplier}px`,
  }
})

const fadeIn = () => ({
  '0%': { opacity: 0 },
  '12.5%': { opacity: 0 },
  '100%': { opacity: 1 },
})

const fadeOut = () => ({
  '0%': { opacity: 1 },
  '12.5%': { opacity: 1 },
  '100%': { opacity: 0 },
})

const fadeInAnimation = (motionSpecs) => ({
  animationName: fadeIn(),
  animationDuration: `${motionSpecs.motionDurations.t2}ms`,
  animationTimingFunction: 'linear',
  animationDelay: '150ms',
  animationFillMode: 'both',
})

const fadeOutAnimation = () => ({
  animationName: fadeOut(),
  animationDuration: '50ms',
  animationTimingFunction: 'linear',
  animationFillMode: 'both',
})

const getFadeAnimation = (isActive, theme) => {
  return isActive ? fadeInAnimation(theme.motionSpecs) : fadeOutAnimation()
}

const SlideContentWrapper = createComponent('SlideContentWrapper', ({ index, slideIndex, nextActiveSlide, theme }) => {
  const isInitialState = slideIndex === nextActiveSlide
  const isActive = index === nextActiveSlide
  const animationStyles = isInitialState ? { opacity: 1 } : getFadeAnimation(isActive, theme)

  return {
    ...animationStyles,
  }
})

const SlideWrapper = createComponent('SlideWrapper', () => ({
  overflow: 'hidden',
}))

const renderServiceMessageDefault = (children) => {
  return (
    <Functional
      item={{
        layoutVariant: 'isc-functional-large',
      }}
    >
      {children}
    </Functional>
  )
}

// Pagination
const renderDots = (dots, carouselRef, autoplayTimerID) => {
  return (
    <DotsContainer
      onClick={() => {
        clearTimeout(autoplayTimerID)
        carouselRef.current.slickPause()
      }}
    >
      {dots}
    </DotsContainer>
  )
}

const customPaging = (i, nextActiveSlide, numberOfItems) => {
  return <Dot key={i} active={i === nextActiveSlide} index={i} lastItem={numberOfItems - 1} />
}

// Swipe function
const handleSwipe = (carouselRef, autoplayTimerID) => {
  clearTimeout(autoplayTimerID)
  carouselRef.current.slickPause()
}

const initAutoPlay = (carouselRef, setAutoplay) => {
  return setTimeout(() => {
    setAutoplay(true)
    carouselRef.current.slickPlay()
  }, AUTO_PLAY_DELAY)
}

const renderServiceMessageCarousel = (
  children,
  theme,
  slideIndex,
  setSlideIndex,
  nextActiveSlide,
  setNextActiveSlide,
  carouselRef,
  autoplay,
  setAutoplay,
  autoplayTimerID,
  setAutoplayTimerID
) => {
  const settings = {
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    speed: 350,
    autoplay,
    autoplaySpeed: AUTO_PLAY_SPEED,
    swipeToSlide: true,
    swipe: true,
    touchThreshold: 1000,
    // Wrapper that renders dots
    appendDots: (dots) => renderDots(dots, carouselRef, autoplayTimerID),
    customPaging: (i) => customPaging(i, nextActiveSlide, children.length),
    beforeChange: (current, next) => {
      setSlideIndex(current)
      setNextActiveSlide(next)
    },
    onSwipe: () => handleSwipe(carouselRef, autoplayTimerID),
    onInit: () => setAutoplayTimerID(initAutoPlay(carouselRef, setAutoplay)),
  }

  return (
    <Slider {...settings} ref={carouselRef}>
      {children &&
        children.map((child, index) => {
          return (
            <SlideWrapper key={index}>
              <SlideContentWrapper key={index} index={index} slideIndex={slideIndex} nextActiveSlide={nextActiveSlide}>
                <Functional
                  item={{
                    layoutVariant: 'isc-functional-large',
                  }}
                  key={index}
                >
                  {child}
                </Functional>
              </SlideContentWrapper>
            </SlideWrapper>
          )
        })}
    </Slider>
  )
}

const ServiceMessageWrapper = ({ item, theme, children }) => {
  const [slideIndex, setSlideIndex] = useState(0)
  const [nextActiveSlide, setNextActiveSlide] = useState(0)
  const [autoplay, setAutoplay] = useState(false)
  const [autoplayTimerID, setAutoplayTimerID] = useState(null)
  const carouselRef = useRef(null)

  const { tags } = item
  const { breakpoints } = theme
  const breakpointsConfig = [
    {
      breakpointMinValue: 0,
      breakpointMaxValue: breakpoints.medium.width,
      value: 'mobile',
    },
  ]

  const screenType = useScreenBreakpoint(breakpointsConfig)
  const isCarousel = screenType === 'mobile' && hasTag(tags, 'ServiceMessage.IsCarousel')

  // prevent timeout autoplay actions on carousel if we resize the window
  useEffect(() => {
    if (autoplayTimerID) {
      clearTimeout(autoplayTimerID)
    }
    // Reset slider position when you resize the screen
    if (nextActiveSlide !== slideIndex && screenType === 'mobile') {
      setSlideIndex(0)
      setNextActiveSlide(0)
    }
  }, [screenType])

  return isCarousel
    ? renderServiceMessageCarousel(
        children,
        theme,
        slideIndex,
        setSlideIndex,
        nextActiveSlide,
        setNextActiveSlide,
        carouselRef,
        autoplay,
        setAutoplay,
        autoplayTimerID,
        setAutoplayTimerID
      )
    : renderServiceMessageDefault(children)
}

ServiceMessageWrapper.layoutVariant = 'nap-homepage-service-message-wrapper'

export default withTheme(ServiceMessageWrapper)
